import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Button, Loading, Search, Tooltip } from '@wfp/ui';

import { useCalendarRangeStart, useEWUser } from '../../hooks/home';
import { userCanEditEvents } from '../../restapi/warningEvent';
import { Link, useNavigate } from 'react-router-dom';
import { RiskIcon } from '../common/RiskIcon';
import { Tipmark } from '../common/Tipmark';
import { formatDateShort, range } from '../../utils/utils';
import {
	Risk,
	Seriousness,
	Warning,
	warningEventTypeClass,
	warningEventTypeLabel,
	WarningInCalendar,
} from '../../restapi/types';
import { addMonths, previousDay, type SimpleDate } from '../../utils/SimpleDate';
import { getCalendar } from '../../restapi/warning';
import { setDataForNewEvent } from '../event/PageEvent';
import { filterCalendar } from './utils';
import { FilterBox } from './FilterBox';
import { SeriousnessDot } from '../common/SeriousnessDot';
import { ApprovedMark } from '../common/ApprovedMark';
import { CommentsMark } from '../common/comments/CommentsMark';

import "./WarningTable.scss";


export function WarningTable() {
	const user = useEWUser();
	const monthsToShow = 6;
	const from = useCalendarRangeStart();
	const to = previousDay(addMonths(from, monthsToShow));

	const calendar = useQuery(['calendar', from, to], () => getCalendar(from, to));

	const navigate = useNavigate();
	const newUpdate = (d: SimpleDate, w: Warning) => {
		setDataForNewEvent(d, w);
		navigate(`/event/0`);
	}

	const [titleFilter, setTitleFilter] = useState(
		localStorage.getItem('titleFilter') || '');
	const [riskFilter, setRiskFilter] = useState<Risk[]>(
		JSON.parse(localStorage.getItem('riskFilter') || '[]'));
	const [seriousnessFilter, setSeriousnessFilter] = useState<Seriousness[]>(
		JSON.parse(localStorage.getItem('seriousnessFilter') || '[]'));
	const [data, setData] = useState<WarningInCalendar[]>([]);

	useEffect(() => {
		const fd = filterCalendar(calendar.data || [], titleFilter, riskFilter, seriousnessFilter);
		setData(fd);
	}, [calendar.data, titleFilter, riskFilter, seriousnessFilter]);

	useEffect(() => {
	    localStorage.setItem('titleFilter', titleFilter);
	}, [titleFilter]);

	useEffect(() => {
	    localStorage.setItem('riskFilter', JSON.stringify(riskFilter));
	}, [riskFilter]);

	useEffect(() => {
	    localStorage.setItem('seriousnessFilter', JSON.stringify(seriousnessFilter));
	}, [seriousnessFilter]);

	if (!user) {
		return null;
	}
	const canEditWarning = userCanEditEvents(user);

	if (!calendar.data) {
		return <Loading withOverlay={false} active small/>;
	}

	return (
		<table className="warning-table">
			<thead>
			<tr>
				<td>
					<Search id="nameFilter"
							value={titleFilter}
							placeholder="Filter by name"
							onChange={v => setTitleFilter(v as unknown as string)}
					/>
				</td>
				<th>
					<FilterBox riskSet={riskFilter} seriousnessSet={seriousnessFilter}
							   onChange={changes => {
								   setRiskFilter(changes.riskSet);
								   setSeriousnessFilter(changes.seriousnessSet);
							   }}/>
				</th>
				{range(monthsToShow).map(i => <td key={`month-${i}`}>
					<Tooltip trigger="click" placement="bottom-end"
							 createRefWrapper={true}
							 content={<div>
								 Preview editions<br/>
								 <Link to={`/doc-preview/${addMonths(from, i)}`}>
									 <Button kind="ghost">Monthly Report</Button></Link><br/>
								 <Link to={`/table-preview/${addMonths(from, i)}`}>
									 <Button kind="ghost">Active Warnings</Button></Link>
							 </div>}>
						<div className="month-btn wfp--link">
							{formatDateShort(addMonths(from, i))} ▾
						</div>
					</Tooltip>
					<div className="fit">MMM-99-V</div>
				</td>)}
			</tr>
			</thead>
			<tbody>
			{data.map(w => <tr key={w.id}>
				<th><Link to={`/timeline/${w.id}`} className="wfp--link">{w.rb} - {w.title}</Link>&nbsp;
					{w.risks.map(r => <RiskIcon key={r} risk={r} seriousness={w.latestEvent.seriousness}/>)}
				</th>
				<td className="no-left-border">
					<Tipmark>
						<p><strong>Raised on: {formatDateShort(w.raised.date)}</strong></p>
						<p className="boxed-text-5">{w.description}</p>
					</Tipmark>
				</td>
				{w.eventsCalendar.map((e, i) => {
					return !e ? <td key={`${w.id}-cell${i}`} className="empty"/>
						: (e === true) ?
							<td key={`${w.id}-cell${i}`} className="add"
								onClick={() => canEditWarning && newUpdate(addMonths(from, i), w)}>✚</td>
							: <td key={e.id} className={'event ' + warningEventTypeClass[e.type]}
								  onClick={() => canEditWarning && navigate(`/event/${e.id}`)}>

								<div className="event-label">
									{warningEventTypeLabel[e.type]}
								</div>
								<div className="icon-bar">
									<SeriousnessDot event={e}/>
									<CommentsMark event={e}/>
									<ApprovedMark event={e}/>
								</div>
							</td>
				})}
			</tr>)}
			</tbody>
		</table>);
}